import { apiRequest } from '../api/http'
import type { ProductChannelListing } from '../products/types'
import type {
  ChannelConnectStartResponse,
  ChannelConnection,
  ChannelConnectionSettingsUpdateRequest,
  ChannelOnboardingJob,
  ChannelOnboardingStockOwner,
  ChannelOrderPullJob,
  ChannelConnectionCreateRequest,
  ChannelWebhookStatus,
  ChannelProductPullRequest,
  ChannelProductPullResult,
  ChannelProductPullJob,
  ChannelProductPullJobCreateRequest,
  ChannelProviderOption,
  ChannelListingStockPushRequest,
  ChannelProductStockPushRequest,
  ChannelPushStockResult,
  ChannelPullOrdersParams,
  CreateProductFromUnmappedChannelListingRequest,
  CreateProductFromUnmappedChannelListingResponse,
  ResolveUnmappedChannelListingRequest,
  ResolveUnmappedChannelListingResponse,
  UnmappedChannelListingPage,
  UnmappedChannelListingParams,
  AmazonOAuthCallbackRequest,
  SallaOAuthCallbackRequest,
  ChannelCredentialsUpdateRequest,
  ChannelStoreMethod,
  ChannelStoreMethodRefreshResult,
  ChannelStoreMethodType,
  NoonConnectionSettings,
  ZidOAuthCallbackRequest,
} from './types'

const CHANNELS_BASE = '/api/channels'
const DELETE_CONFIRMATION_HEADERS = { 'X-Confirm-Channel-Delete': 'products-and-orders' }

export interface DeleteChannelConnectionOptions {
  confirmedProductsAndOrdersDeletion: boolean
}

function channelAuth(accessToken: string) {
  return { accessToken }
}

export function listChannelProviders(accessToken: string): Promise<ChannelProviderOption[]> {
  return apiRequest<ChannelProviderOption[]>(CHANNELS_BASE, '/providers', channelAuth(accessToken))
}

export function refreshNoonListing(accessToken: string, connectionId: number, listingId: number): Promise<ProductChannelListing> {
  return apiRequest<ProductChannelListing>(CHANNELS_BASE, `/${connectionId}/listings/${listingId}/noon/refresh`, { method: 'POST', accessToken })
}

export function refreshChannelListingStock(accessToken: string, connectionId: number, listingId: number): Promise<void> {
  return apiRequest<void>(CHANNELS_BASE, `/${connectionId}/listings/${listingId}/stock/refresh`, { method: 'POST', accessToken })
}

export function listChannelConnections(accessToken: string): Promise<ChannelConnection[]> {
  return apiRequest<ChannelConnection[]>(CHANNELS_BASE, '', channelAuth(accessToken))
}

export function getChannelConnection(accessToken: string, id: number): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, `/${id}`, channelAuth(accessToken))
}

export function updateChannelConnectionSettings(
  accessToken: string,
  id: number,
  payload: ChannelConnectionSettingsUpdateRequest,
): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, `/${id}/settings`, {
    method: 'PUT',
    body: payload,
    accessToken,
  })
}

export function listChannelStoreMethods(
  accessToken: string,
  id: number,
  type?: ChannelStoreMethodType,
): Promise<ChannelStoreMethod[]> {
  const query = type ? `?${new URLSearchParams({ type }).toString()}` : ''
  return apiRequest<ChannelStoreMethod[]>(CHANNELS_BASE, `/${id}/store-methods${query}`, channelAuth(accessToken))
}

export function refreshChannelStoreMethods(accessToken: string, id: number): Promise<ChannelStoreMethodRefreshResult> {
  return apiRequest<ChannelStoreMethodRefreshResult>(CHANNELS_BASE, `/${id}/store-methods/refresh`, { method: 'POST', accessToken })
}

export function createChannelConnection(accessToken: string, payload: ChannelConnectionCreateRequest): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, '', { method: 'POST', body: payload, accessToken })
}

export function deleteChannelConnection(
  accessToken: string,
  id: number,
  options: DeleteChannelConnectionOptions,
): Promise<void> {
  return apiRequest<void>(CHANNELS_BASE, `/${id}`, {
    method: 'DELETE',
    accessToken,
    headers: options.confirmedProductsAndOrdersDeletion ? DELETE_CONFIRMATION_HEADERS : undefined,
  })
}

export function startChannelConnect(accessToken: string, id: number): Promise<ChannelConnectStartResponse> {
  return apiRequest<ChannelConnectStartResponse>(CHANNELS_BASE, `/${id}/connect`, { method: 'POST', accessToken })
}

export function storeChannelCredentials(
  accessToken: string,
  id: number,
  payload: ChannelCredentialsUpdateRequest,
): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, `/${id}/credentials`, { method: 'POST', body: payload, accessToken })
}

export function getNoonConnectionSettings(accessToken: string, id: number): Promise<NoonConnectionSettings> {
  return apiRequest<NoonConnectionSettings>(CHANNELS_BASE, `/${id}/settings/noon`, channelAuth(accessToken))
}

export function updateNoonConnectionSettings(
  accessToken: string,
  id: number,
  payload: NoonConnectionSettings,
): Promise<NoonConnectionSettings> {
  return apiRequest<NoonConnectionSettings>(CHANNELS_BASE, `/${id}/settings/noon`, {
    method: 'PUT',
    body: payload,
    accessToken,
  })
}

export function completeZidOAuthCallback(accessToken: string, payload: ZidOAuthCallbackRequest): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, '/zid/oauth/callback', { method: 'POST', body: payload, accessToken })
}

export function completeSallaOAuthCallback(accessToken: string, payload: SallaOAuthCallbackRequest): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, '/salla/oauth/callback', { method: 'POST', body: payload, accessToken })
}

export function completeAmazonOAuthCallback(accessToken: string, payload: AmazonOAuthCallbackRequest): Promise<ChannelConnection> {
  return apiRequest<ChannelConnection>(CHANNELS_BASE, '/amazon/oauth/callback', { method: 'POST', body: payload, accessToken })
}

export function pullChannelProducts(
  accessToken: string,
  id: number,
  payload: ChannelProductPullRequest,
): Promise<ChannelProductPullResult> {
  return apiRequest<ChannelProductPullResult>(CHANNELS_BASE, `/${id}/products/pull`, { method: 'POST', body: payload, accessToken })
}

export function enqueueChannelProductPullJob(
  accessToken: string,
  id: number,
  payload: ChannelProductPullJobCreateRequest,
): Promise<ChannelProductPullJob> {
  return apiRequest<ChannelProductPullJob>(CHANNELS_BASE, `/${id}/products/pull-jobs`, { method: 'POST', body: payload, accessToken })
}

export function getLatestChannelProductPullJob(accessToken: string, id: number): Promise<ChannelProductPullJob> {
  return apiRequest<ChannelProductPullJob>(CHANNELS_BASE, `/${id}/products/pull-job/latest`, channelAuth(accessToken))
}

export function listLatestChannelProductPullJobs(accessToken: string): Promise<ChannelProductPullJob[]> {
  return apiRequest<ChannelProductPullJob[]>(CHANNELS_BASE, '/product-pull-jobs/latest', channelAuth(accessToken))
}

export function retryChannelProductPullJob(accessToken: string, jobId: number): Promise<ChannelProductPullJob> {
  return apiRequest<ChannelProductPullJob>(CHANNELS_BASE, `/product-pull-jobs/${jobId}/retry`, { method: 'POST', accessToken })
}

export function pushChannelListingStock(
  accessToken: string,
  productId: number,
  listingId: number,
  payload: ChannelListingStockPushRequest,
): Promise<ChannelPushStockResult> {
  return apiRequest<ChannelPushStockResult>(
    CHANNELS_BASE,
    `/products/${productId}/listings/${listingId}/stock/push`,
    { method: 'POST', body: payload, accessToken },
  )
}

export function pushChannelProductStock(
  accessToken: string,
  connectionId: number,
  productId: number,
  payload: ChannelProductStockPushRequest,
): Promise<ChannelPushStockResult> {
  return apiRequest<ChannelPushStockResult>(
    CHANNELS_BASE,
    `/${connectionId}/products/${productId}/stock/push`,
    { method: 'POST', body: payload, accessToken },
  )
}

export function listUnmappedChannelListings(
  accessToken: string,
  id: number,
  params: UnmappedChannelListingParams = {},
): Promise<UnmappedChannelListingPage> {
  const query = new URLSearchParams()
  if (params.page != null) query.set('page', String(params.page))
  if (params.size != null) query.set('size', String(params.size))
  if (params.query?.trim()) query.set('query', params.query.trim())
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiRequest<UnmappedChannelListingPage>(CHANNELS_BASE, `/${id}/unmapped-listings${suffix}`, channelAuth(accessToken))
}

export function resolveUnmappedChannelListing(
  accessToken: string,
  listingId: number,
  payload: ResolveUnmappedChannelListingRequest,
): Promise<ResolveUnmappedChannelListingResponse> {
  return apiRequest<ResolveUnmappedChannelListingResponse>(
    CHANNELS_BASE,
    `/unmapped-listings/${listingId}/resolve`,
    { method: 'POST', body: payload, accessToken },
  )
}

export function createProductFromUnmappedChannelListing(
  accessToken: string,
  listingId: number,
  payload: CreateProductFromUnmappedChannelListingRequest,
): Promise<CreateProductFromUnmappedChannelListingResponse> {
  return apiRequest<CreateProductFromUnmappedChannelListingResponse>(
    CHANNELS_BASE,
    `/unmapped-listings/${listingId}/create-product`,
    { method: 'POST', body: payload, accessToken },
  )
}

export function enqueueChannelOrderPullJob(
  accessToken: string,
  id: number,
  params: ChannelPullOrdersParams = {},
): Promise<ChannelOrderPullJob> {
  const query = channelPullOrdersQuery(params)
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiRequest<ChannelOrderPullJob>(CHANNELS_BASE, `/${id}/orders/pull-jobs${suffix}`, { method: 'POST', accessToken })
}

export function getLatestChannelOrderPullJob(accessToken: string, id: number): Promise<ChannelOrderPullJob> {
  return apiRequest<ChannelOrderPullJob>(CHANNELS_BASE, `/${id}/orders/pull-job/latest`, channelAuth(accessToken))
}

export function listLatestChannelOrderPullJobs(accessToken: string): Promise<ChannelOrderPullJob[]> {
  return apiRequest<ChannelOrderPullJob[]>(CHANNELS_BASE, '/order-pull-jobs/latest', channelAuth(accessToken))
}

function channelPullOrdersQuery(params: ChannelPullOrdersParams): URLSearchParams {
  const query = new URLSearchParams()
  if (params.cursor) query.set('cursor', params.cursor)
  if (params.dateFrom) query.set('dateFrom', params.dateFrom)
  if (params.dateTo) query.set('dateTo', params.dateTo)
  return query
}

export function getChannelWebhookStatus(accessToken: string, id: number): Promise<ChannelWebhookStatus> {
  return apiRequest<ChannelWebhookStatus>(CHANNELS_BASE, `/${id}/webhooks/status`, channelAuth(accessToken))
}

export function registerChannelWebhooks(accessToken: string, id: number): Promise<ChannelWebhookStatus> {
  return apiRequest<ChannelWebhookStatus>(CHANNELS_BASE, `/${id}/webhooks/register`, { method: 'POST', accessToken })
}

export function getLatestChannelOnboardingJob(accessToken: string, id: number): Promise<ChannelOnboardingJob> {
  return apiRequest<ChannelOnboardingJob>(CHANNELS_BASE, `/${id}/onboarding-job/latest`, channelAuth(accessToken))
}

export function listLatestChannelOnboardingJobs(accessToken: string): Promise<ChannelOnboardingJob[]> {
  return apiRequest<ChannelOnboardingJob[]>(CHANNELS_BASE, '/onboarding-jobs/latest', channelAuth(accessToken))
}

export function chooseChannelOnboardingStockOwner(
  accessToken: string,
  jobId: number,
  stockOwner: ChannelOnboardingStockOwner,
): Promise<ChannelOnboardingJob> {
  return apiRequest<ChannelOnboardingJob>(CHANNELS_BASE, `/onboarding-jobs/${jobId}/stock-owner`, {
    method: 'PUT',
    accessToken,
    body: { stockOwner },
  })
}

export function retryChannelOnboardingJob(accessToken: string, jobId: number): Promise<ChannelOnboardingJob> {
  return apiRequest<ChannelOnboardingJob>(CHANNELS_BASE, `/onboarding-jobs/${jobId}/retry`, { method: 'POST', accessToken })
}
